/**
 * AI Assistant Bubble — Floating chat panel accessible from every page
 * 3 tabs: Copilot, Prospecting, Content Generator
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  MessageSquare, X, Send, Sparkles, Target, FileText,
  Copy, Check, RotateCcw, Loader2, ChevronDown
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import useAuthStore from '../lib/auth-store';

const API = process.env.REACT_APP_BACKEND_URL;

const MODES = [
  { id: 'copilot', label: 'Ask Jarvis', icon: Sparkles, desc: 'Hardware, setup, MSC procedures, pricing programs and pitches' },
  { id: 'prospect', label: 'Prospect', icon: Target, desc: 'Scripts, rebuttals & qualifying questions' },
  { id: 'content', label: 'Content', icon: FileText, desc: 'Emails, quotes & guides' },
];

const QUICK_PROMPTS = {
  copilot: [
    'Best rebuttal for "I\'m happy with my processor"',
    'Explain dual pricing vs cash discount',
    'What questions should I ask a restaurant owner?',
    'How do I calculate effective rate from a statement?',
  ],
  prospect: [
    'Cold call script for restaurants — Dual Pricing',
    'Door-to-door pitch for retail — POS System',
    'Qualifying questions for medical offices',
    'Objection rebuttals for auto repair shops',
  ],
  content: [
    'Write a proposal follow-up email',
    'Pricing comparison: current 3.2% vs our IC+',
    'Clover terminal setup guide for merchants',
    'Win-back email for churned merchant',
  ],
};

const srcLine = (s) => `${s.title} — ${s.pack}${s.page ? `, p. ${s.page}` : ''}${s.section ? `, “${s.section}”` : ''}${s.captured_at ? `, captured ${new Date(s.captured_at).toLocaleDateString()}` : ''} (v${s.version})`;

export default function AIAssistantBubble() {
  const { user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('copilot');
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [copied, setCopied] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (open) scrollToBottom();
  }, [messages, open, scrollToBottom]);

  const handleSend = async (text) => {
    const msg = text || input.trim();
    if (!msg || streaming) return;

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please log in first');
      return;
    }

    const userMsg = { role: 'user', content: msg };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setStreaming(true);

    // Add loading assistant message
    setMessages(prev => [...prev, { role: 'assistant', content: '', streaming: true }]);

    try {
      const response = await fetch(`${API}/api/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: msg,
          session_id: sessionId,
          mode: mode,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Jarvis is unavailable right now — please try again in a moment.');
      }

      const data = await response.json();
      const aiText = data.response || '';

      setMessages(prev => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;
        if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
          updated[lastIdx] = { role: 'assistant', content: aiText, streaming: false, sources: data.sources || [], answerMode: data.answer_mode };
        }
        return updated;
      });

      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
      }
    } catch (err) {
      setMessages(prev => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;
        if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
          updated[lastIdx] = { role: 'assistant', content: `Error: ${err.message}`, streaming: false };
        }
        return updated;
      });
    } finally {
      setStreaming(false);
    }
  };

  const handleCopy = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopied(idx);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleNewChat = () => {
    setMessages([]);
    setSessionId(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Simple markdown-ish rendering
  const renderContent = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    return lines.map((line, i) => {
      if (line.startsWith('### ')) return <h4 key={i} className="font-semibold text-sm mt-3 mb-1">{line.slice(4)}</h4>;
      if (line.startsWith('## ')) return <h3 key={i} className="font-bold text-sm mt-3 mb-1">{line.slice(3)}</h3>;
      if (line.startsWith('# ')) return <h3 key={i} className="font-bold text-base mt-3 mb-1">{line.slice(2)}</h3>;
      if (line.startsWith('- ') || line.startsWith('* ')) return <li key={i} className="ml-4 text-xs list-disc">{renderInline(line.slice(2))}</li>;
      if (line.match(/^\d+\./)) return <li key={i} className="ml-4 text-xs list-decimal">{renderInline(line.replace(/^\d+\.\s*/, ''))}</li>;
      if (line.trim() === '') return <br key={i} />;
      return <p key={i} className="text-xs leading-relaxed">{renderInline(line)}</p>;
    });
  };

  const renderInline = (text) => {
    // Bold
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, i) => i % 2 === 1 ? <strong key={i}>{part}</strong> : part);
  };

  return (
    <>
      {/* Floating Bubble Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ai-bubble-btn"
          className="fixed bottom-6 right-6 w-14 h-14 bg-[#2563EB] hover:bg-[#1D4ED8] rounded-full shadow-[0_4px_24px_rgba(37,99,235,0.4)] flex items-center justify-center text-white transition-all hover:scale-105 z-50 group"
        >
          <Sparkles className="h-6 w-6 group-hover:rotate-12 transition-transform" />
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 w-[420px] h-[600px] bg-background border rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden"
          data-testid="ai-panel"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b bg-card flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#2563EB] rounded-lg flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold" data-testid="jarvis-title">Jarvis</h3>
                <p className="text-[10px] text-muted-foreground">MSC knowledge &amp; guidance{user?.role === 'super_admin' && <> · <Link to="/admin/jarvis-knowledge" onClick={() => setOpen(false)} className="text-[#2563EB] hover:underline" data-testid="jarvis-manage-knowledge-link">Manage knowledge</Link></>}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleNewChat} title="New Chat" data-testid="ai-new-chat">
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setOpen(false)} data-testid="ai-close-btn">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Mode Tabs */}
          <div className="flex border-b shrink-0 bg-card">
            {MODES.map(m => (
              <button
                key={m.id}
                onClick={() => { setMode(m.id); handleNewChat(); }}
                data-testid={`ai-tab-${m.id}`}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[11px] font-medium transition-colors border-b-2 ${
                  mode === m.id
                    ? 'border-[#2563EB] text-[#2563EB]'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <m.icon className="h-3.5 w-3.5" />
                {m.label}
              </button>
            ))}
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              <div className="space-y-4">
                <div className="text-center py-4">
                  <div className="w-12 h-12 bg-[#2563EB]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    {MODES.find(m => m.id === mode)?.icon && React.createElement(MODES.find(m => m.id === mode).icon, { className: "h-5 w-5 text-[#2563EB]" })}
                  </div>
                  <p className="text-sm font-medium">{MODES.find(m => m.id === mode)?.desc}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Quick Prompts</p>
                  {(QUICK_PROMPTS[mode] || []).map((prompt, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSend(prompt)}
                      data-testid={`quick-prompt-${idx}`}
                      className="w-full text-left px-3 py-2.5 bg-card hover:bg-accent/50 border rounded-lg text-xs transition-colors"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[85%] ${
                    msg.role === 'user'
                      ? 'bg-[#2563EB] text-white rounded-2xl rounded-br-md px-3.5 py-2.5'
                      : 'bg-card border rounded-2xl rounded-bl-md px-3.5 py-2.5'
                  }`}>
                    {msg.role === 'user' ? (
                      <p className="text-xs leading-relaxed">{msg.content}</p>
                    ) : (
                      <div className="space-y-1">
                        {renderContent(msg.content)}
                        {msg.streaming && <Loader2 className="h-3 w-3 animate-spin mt-1 text-muted-foreground" />}
                        {!msg.streaming && msg.sources?.length > 0 && (
                          <p className="mt-2 border-t pt-1.5 text-[10px] text-muted-foreground" data-testid={`jarvis-sources-${idx}`}>
                            Sources: {msg.sources.map((s) => `[${s.n}] ${srcLine(s)}`).join(' · ')}
                          </p>
                        )}
                        {!msg.streaming && msg.content && (
                          <button
                            onClick={() => handleCopy(msg.content, idx)}
                            className="mt-2 flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {copied === idx ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                            {copied === idx ? 'Copied' : 'Copy'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="px-4 py-3 border-t bg-card shrink-0">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={mode === 'prospect' ? 'Describe the product & niche...' : mode === 'content' ? 'What content do you need?' : 'Ask Jarvis…'}
                className="flex-1 bg-background border rounded-xl px-3.5 py-2.5 text-xs resize-none max-h-24 focus:outline-none focus:ring-1 focus:ring-[#2563EB]"
                rows={1}
                data-testid="ai-input"
              />
              <Button
                size="icon"
                className="h-9 w-9 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8] shrink-0"
                onClick={() => handleSend()}
                disabled={!input.trim() || streaming}
                data-testid="ai-send-btn"
              >
                {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-[9px] text-muted-foreground mt-1.5 text-center">
              AI can make mistakes. Verify important information.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
